import { useState, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Grid,
  IconButton,
  CircularProgress,
  alpha,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Tooltip,
  TextField,
  Autocomplete,
  Alert,
  Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RouteIcon from '@mui/icons-material/Route';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MapIcon from '@mui/icons-material/Map';
import ReplayIcon from '@mui/icons-material/Replay';
import TrafficIcon from '@mui/icons-material/Traffic';
import EditNoteIcon from '@mui/icons-material/EditNote';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';
import axiosInstance from '../../lib/axios';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { GoogleMapsWrapper, MapMarker } from '../../components/GoogleMapsWrapper';
import { DeliveryStopManageDialog } from './DeliveryStopManageDialog';
import { useSnackbar } from '../../components/SnackbarProvider';

interface DeliveryRoute {
  id: string;
  name: string;
  status: 'draft' | 'planned' | 'in_progress' | 'completed' | 'cancelled';
  driverId: string | null;
  driver: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  originLocation: {
    id: string;
    name: string;
    address: string;
  };
  originLat: number;
  originLng: number;
  returnToOrigin: boolean;
  returnLat: number | null;
  returnLng: number | null;
  deliveryIds: string[];
  routeData: {
    optimizedOrder: number[];
    waypoints: { deliveryId: string; lat: number; lng: number; address?: string }[];
    legs: {
      origin: string;
      destination: string;
      distanceKm: number;
      durationMinutes: number;
    }[];
    polyline: string;
  };
  totalDistanceKm: number;
  totalDurationMinutes: number;
  trafficAware: boolean;
  departureTime: string | null;
  scheduledDate: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  createdBy: {
    firstName: string;
    lastName: string;
  } | null;
}

const fetchRoute = async (id: string): Promise<DeliveryRoute> => {
  const response = await axiosInstance.get(`/delivery-routes/${id}`);
  return response.data;
};

const updateRouteStatus = async ({
  id,
  status,
}: {
  id: string;
  status: string;
}): Promise<DeliveryRoute> => {
  const response = await axiosInstance.patch(`/delivery-routes/${id}/status`, { status });
  return response.data;
};

interface Driver {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface Delivery {
  id: string;
  status: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  deliveryAddress: {
    street: string;
    city: string;
    state?: string;
    postalCode?: string;
    country?: string;
    notes?: string;
  };
  notes?: string;
  driverNotes?: string;
  adminNotes?: string;
  failureReason?: string;
  proofOfDelivery?: Array<{
    type: 'signature' | 'photo';
    filePath: string;
    fileName: string;
    uploadedBy: string;
    uploadedAt: string;
  }>;
}

const fetchDrivers = async (): Promise<Driver[]> => {
  const response = await axiosInstance.get('/users/drivers');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchDelivery = async (deliveryId: string): Promise<Delivery> => {
  const response = await axiosInstance.get(`/deliveries/${deliveryId}`);
  return response.data;
};

const fetchDeliveries = async (deliveryIds: string[]): Promise<Delivery[]> => {
  if (!deliveryIds || deliveryIds.length === 0) return [];
  const promises = deliveryIds.map((id) => fetchDelivery(id).catch(() => null));
  const results = await Promise.all(promises);
  return results.filter((d): d is Delivery => d !== null);
};

const assignDriver = async ({
  id,
  driverId,
}: {
  id: string;
  driverId: string;
}): Promise<DeliveryRoute> => {
  const response = await axiosInstance.patch(`/delivery-routes/${id}/assign-driver`, { driverId });
  return response.data;
};

const updateSchedule = async ({
  id,
  scheduledDate,
  departureTime,
}: {
  id: string;
  scheduledDate: string;
  departureTime?: string;
}): Promise<DeliveryRoute> => {
  const response = await axiosInstance.patch(`/delivery-routes/${id}/schedule`, {
    scheduledDate,
    departureTime,
  });
  return response.data;
};

export const RouteShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const { data: route, isLoading, error } = useQuery({
    queryKey: ['delivery-route', id],
    queryFn: () => fetchRoute(id!),
    enabled: !!id,
  });

  const statusMutation = useMutation({
    mutationFn: updateRouteStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-route', id] });
      queryClient.invalidateQueries({ queryKey: ['delivery-routes'] });
      showMessage('Route status updated', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update route status', 'error');
    },
  });

  // Fetch drivers for assignment (when route needs setup - draft or planned without driver)
  const { data: drivers = [] } = useQuery({
    queryKey: ['drivers'],
    queryFn: fetchDrivers,
    enabled: (route?.status === 'draft' || route?.status === 'planned') && !route?.driverId,
  });

  // State for draft route editing
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [departureTime, setDepartureTime] = useState<string>('');

  // State for delivery stop management dialog
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);
  const [selectedStopNumber, setSelectedStopNumber] = useState<number>(0);
  const [deliveryStopsExpanded, setDeliveryStopsExpanded] = useState(true);

  // Fetch deliveries for the route
  const { data: deliveries = [] } = useQuery({
    queryKey: ['route-deliveries', route?.deliveryIds],
    queryFn: () => fetchDeliveries(route?.deliveryIds || []),
    enabled: !!route?.deliveryIds && route.deliveryIds.length > 0,
  });

  // Create a map of deliveryId to delivery for easy lookup
  const deliveryMap = useMemo(() => {
    const map = new Map<string, Delivery>();
    deliveries.forEach((d) => map.set(d.id, d));
    return map;
  }, [deliveries]);

  // Calculate delivery completion stats
  const deliveryStats = useMemo(() => {
    const completed = deliveries.filter((d) => d.status === 'delivered').length;
    const failed = deliveries.filter((d) => d.status === 'failed').length;
    const total = deliveries.length;
    return { completed, failed, total, pending: total - completed - failed };
  }, [deliveries]);

  const handleOpenManageDialog = (delivery: Delivery, stopNumber: number) => {
    setSelectedDelivery(delivery);
    setSelectedStopNumber(stopNumber);
    setManageDialogOpen(true);
  };

  const handleCloseManageDialog = () => {
    setManageDialogOpen(false);
    setSelectedDelivery(null);
  };

  const driverMutation = useMutation({
    mutationFn: assignDriver,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-route', id] });
      queryClient.invalidateQueries({ queryKey: ['delivery-routes'] });
      showMessage('Driver assigned successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to assign driver', 'error');
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: updateSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-route', id] });
      queryClient.invalidateQueries({ queryKey: ['delivery-routes'] });
      showMessage('Schedule updated successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update schedule', 'error');
    },
  });

  const missingFields: string[] = [];
  if (route && (route.status === 'draft' || route.status === 'planned')) {
    if (!route.driverId) missingFields.push('driver');
    if (!route.scheduledDate) missingFields.push('scheduled date');
  }
  const needsSetup = missingFields.length > 0;

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'draft':
        return palette.accent;
      case 'planned':
        return palette.info;
      case 'in_progress':
        return palette.warning;
      case 'completed':
        return palette.success;
      case 'cancelled':
        return palette.textMuted;
      default:
        return palette.textMuted;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <EditNoteIcon sx={{ fontSize: 18 }} />;
      case 'planned':
        return <ScheduleIcon sx={{ fontSize: 18 }} />;
      case 'in_progress':
        return <PlayArrowIcon sx={{ fontSize: 18 }} />;
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 18 }} />;
      case 'cancelled':
        return <CancelIcon sx={{ fontSize: 18 }} />;
      default:
        return null;
    }
  };

  const formatStatusLabel = (status: string): string => {
    return status.replace(/_/g, ' ').toUpperCase();
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-DZ', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper to safely convert to number (database decimals come as strings)
  const toNum = (val: string | number | null | undefined): number => {
    if (val === null || val === undefined) return 0;
    return typeof val === 'number' ? val : parseFloat(val);
  };

  // Build map markers from route data
  const mapMarkers: MapMarker[] = route
    ? [
        // Origin marker
        {
          id: 'origin',
          position: { lat: toNum(route.originLat), lng: toNum(route.originLng) },
          title: route.originLocation?.name || 'Origin',
          icon: 'store',
          info: (
            <Box>
              <Typography variant="subtitle2">{route.originLocation?.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {route.returnToOrigin ? 'Start & End Point' : 'Start Point'}
              </Typography>
            </Box>
          ),
        },
        // Delivery waypoints
        ...(route.routeData?.waypoints || []).map((wp, index) => ({
          id: wp.deliveryId,
          position: { lat: toNum(wp.lat), lng: toNum(wp.lng) },
          title: `Stop ${index + 1}`,
          label: String(index + 1),
          icon: 'delivery' as const,
          info: (
            <Box>
              <Typography variant="subtitle2">Stop {index + 1}</Typography>
              {wp.address && (
                <Typography variant="caption" color="text.secondary">
                  {wp.address}
                </Typography>
              )}
            </Box>
          ),
        })),
      ]
    : [];

  const colors =
    mode === 'dark'
      ? {
          bg: palette.background,
          surface: palette.surface,
          surfaceElevated: palette.surfaceElevated,
          border: palette.border,
          borderLight: palette.borderLight,
          textPrimary: palette.textPrimary,
          textSecondary: palette.textSecondary,
          textMuted: palette.textMuted,
        }
      : {
          bg: palette.light.background,
          surface: palette.light.surface,
          surfaceElevated: palette.light.surfaceElevated,
          border: palette.light.border,
          borderLight: palette.light.borderLight,
          textPrimary: palette.light.textPrimary,
          textSecondary: palette.light.textSecondary,
          textMuted: palette.light.textMuted,
        };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  if (error || !route) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <RouteIcon sx={{ fontSize: 80, color: colors.textMuted, mb: 2 }} />
        <Typography variant="h5" sx={{ color: colors.textPrimary, mb: 1 }}>
          Route Not Found
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/deliveries?tab=routes')}
          sx={{ mt: 2 }}
        >
          Back to Routes
        </Button>
      </Box>
    );
  }

  const statusColor = getStatusColor(route.status);

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 3, ...animations.slideDown }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/deliveries?tab=routes')}
          sx={{
            mb: 2,
            color: colors.textMuted,
            fontFamily: typography.fontFamily.mono,
            '&:hover': { color: colors.textPrimary },
          }}
        >
          Back to Routes
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: theme.radius.sm,
                backgroundColor: alpha(statusColor, 0.1),
                border: `2px solid ${statusColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RouteIcon sx={{ color: statusColor, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.extrabold,
                  color: colors.textPrimary,
                  letterSpacing: '-0.02em',
                }}
              >
                {route.name || `Route ${route.id.substring(0, 8).toUpperCase()}`}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  icon={getStatusIcon(route.status) || undefined}
                  label={formatStatusLabel(route.status)}
                  sx={{
                    backgroundColor: alpha(statusColor, 0.1),
                    color: statusColor,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    border: `1px solid ${statusColor}`,
                    '& .MuiChip-icon': { color: statusColor },
                  }}
                />
                {route.returnToOrigin && (
                  <Chip
                    icon={<ReplayIcon />}
                    label="Round Trip"
                    size="small"
                    sx={{
                      backgroundColor: alpha(palette.accent, 0.1),
                      color: palette.accent,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: '10px',
                      '& .MuiChip-icon': { color: palette.accent, fontSize: 14 },
                    }}
                  />
                )}
                {route.trafficAware && (
                  <Chip
                    icon={<TrafficIcon />}
                    label="Traffic-Aware"
                    size="small"
                    sx={{
                      backgroundColor: alpha(palette.primary, 0.1),
                      color: palette.primary,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: '10px',
                      '& .MuiChip-icon': { color: palette.primary, fontSize: 14 },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {route.status === 'draft' && (
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => statusMutation.mutate({ id: route.id, status: 'cancelled' })}
                disabled={statusMutation.isPending}
                sx={{
                  borderColor: palette.error,
                  color: palette.error,
                  fontFamily: typography.fontFamily.mono,
                  '&:hover': { backgroundColor: alpha(palette.error, 0.1) },
                }}
              >
                Cancel
              </Button>
            )}
            {route.status === 'planned' && (
              <>
                <Tooltip title={needsSetup ? `Missing: ${missingFields.join(', ')}` : ''}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => statusMutation.mutate({ id: route.id, status: 'in_progress' })}
                      disabled={statusMutation.isPending || needsSetup}
                      sx={{
                        backgroundColor: palette.success,
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.bold,
                        '&:hover': { backgroundColor: alpha(palette.success, 0.9) },
                        '&.Mui-disabled': {
                          backgroundColor: alpha(palette.success, 0.3),
                        },
                      }}
                    >
                      Start Route
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => statusMutation.mutate({ id: route.id, status: 'cancelled' })}
                  disabled={statusMutation.isPending}
                  sx={{
                    borderColor: palette.error,
                    color: palette.error,
                    fontFamily: typography.fontFamily.mono,
                    '&:hover': { backgroundColor: alpha(palette.error, 0.1) },
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
            {route.status === 'in_progress' && (
              <>
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => statusMutation.mutate({ id: route.id, status: 'completed' })}
                  disabled={statusMutation.isPending}
                  sx={{
                    backgroundColor: palette.success,
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.bold,
                    '&:hover': { backgroundColor: alpha(palette.success, 0.9) },
                  }}
                >
                  Mark Completed
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => statusMutation.mutate({ id: route.id, status: 'cancelled' })}
                  disabled={statusMutation.isPending}
                  sx={{
                    borderColor: palette.error,
                    color: palette.error,
                    fontFamily: typography.fontFamily.mono,
                    '&:hover': { backgroundColor: alpha(palette.error, 0.1) },
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Route Setup Alert - shown when missing required fields */}
      {needsSetup && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{
            mb: 3,
            backgroundColor: alpha(palette.warning, 0.1),
            border: `1px solid ${alpha(palette.warning, 0.3)}`,
            '& .MuiAlert-icon': { color: palette.warning },
            ...animations.slideUp(0.05),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            This route cannot be started yet. Please assign a driver and set a scheduled date.
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            Missing: {missingFields.join(', ')}
          </Typography>
        </Alert>
      )}

      {/* Route Setup Card - shown when missing required fields */}
      {needsSetup && (
        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${palette.warning}`,
            borderRadius: theme.radius.sm,
            mb: 3,
            overflow: 'hidden',
            ...animations.slideUp(0.1),
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: alpha(palette.warning, 0.05),
            }}
          >
            <EditNoteIcon sx={{ color: palette.warning }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wide,
                color: palette.warning,
              }}
            >
              Complete Route Setup
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Driver Assignment */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    letterSpacing: typography.letterSpacing.wide,
                    color: colors.textMuted,
                  }}
                >
                  Assign Driver *
                </Typography>
                {route.driver ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: alpha(palette.success, 0.05), borderRadius: theme.radius.xs, border: `1px solid ${alpha(palette.success, 0.2)}` }}>
                    <Avatar sx={{ backgroundColor: palette.success }}>
                      {route.driver.firstName?.[0]}
                      {route.driver.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {route.driver.firstName} {route.driver.lastName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.textMuted }}>
                        {route.driver.email}
                      </Typography>
                    </Box>
                    <CheckCircleIcon sx={{ color: palette.success, ml: 'auto' }} />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Autocomplete
                      fullWidth
                      options={drivers}
                      getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                      value={selectedDriver}
                      onChange={(_event, newValue) => setSelectedDriver(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select a driver..."
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: colors.surfaceElevated,
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, backgroundColor: palette.primary, fontSize: '12px' }}>
                            {option.firstName?.[0]}{option.lastName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{option.firstName} {option.lastName}</Typography>
                            <Typography variant="caption" sx={{ color: colors.textMuted }}>{option.email}</Typography>
                          </Box>
                        </Box>
                      )}
                    />
                    <Button
                      variant="contained"
                      disabled={!selectedDriver || driverMutation.isPending}
                      onClick={() => {
                        if (selectedDriver) {
                          driverMutation.mutate({ id: route.id, driverId: selectedDriver.id });
                          setSelectedDriver(null);
                        }
                      }}
                      sx={{
                        backgroundColor: palette.primary,
                        fontFamily: typography.fontFamily.mono,
                        minWidth: 100,
                      }}
                    >
                      {driverMutation.isPending ? <CircularProgress size={20} /> : 'Assign'}
                    </Button>
                  </Box>
                )}
              </Grid>

              {/* Schedule */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    letterSpacing: typography.letterSpacing.wide,
                    color: colors.textMuted,
                  }}
                >
                  Schedule Delivery *
                </Typography>
                {route.scheduledDate ? (
                  <Box sx={{ p: 2, backgroundColor: alpha(palette.success, 0.05), borderRadius: theme.radius.xs, border: `1px solid ${alpha(palette.success, 0.2)}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon sx={{ color: palette.success }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {new Date(route.scheduledDate).toLocaleDateString('fr-DZ', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                        </Typography>
                        {route.departureTime && (
                          <Typography variant="caption" sx={{ color: colors.textMuted }}>
                            Departure: {new Date(route.departureTime).toLocaleTimeString('fr-DZ', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        )}
                      </Box>
                      <CheckCircleIcon sx={{ color: palette.success, ml: 'auto' }} />
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                      type="date"
                      size="small"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        flex: 1,
                        minWidth: 150,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: colors.surfaceElevated,
                        },
                      }}
                    />
                    <TextField
                      type="time"
                      size="small"
                      value={departureTime}
                      onChange={(e) => setDepartureTime(e.target.value)}
                      placeholder="Departure time (optional)"
                      sx={{
                        width: 120,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: colors.surfaceElevated,
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      disabled={!scheduledDate || scheduleMutation.isPending}
                      onClick={() => {
                        if (scheduledDate) {
                          scheduleMutation.mutate({
                            id: route.id,
                            scheduledDate,
                            departureTime: departureTime ? `${scheduledDate}T${departureTime}:00` : undefined,
                          });
                          setScheduledDate('');
                          setDepartureTime('');
                        }
                      }}
                      sx={{
                        backgroundColor: palette.primary,
                        fontFamily: typography.fontFamily.mono,
                        minWidth: 80,
                      }}
                    >
                      {scheduleMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>

          </Box>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Route Info */}
        <Grid item xs={12} md={5}>
          {/* Statistics Card */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              mb: 3,
              overflow: 'hidden',
              ...animations.slideUp(0.1),
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <RouteIcon sx={{ color: palette.primary }} />
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  letterSpacing: typography.letterSpacing.wide,
                }}
              >
                Route Statistics
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: alpha(palette.info, 0.05),
                      borderRadius: theme.radius.xs,
                      border: `1px solid ${alpha(palette.info, 0.2)}`,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: '10px',
                      }}
                    >
                      Total Distance
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: palette.info,
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                      }}
                    >
                      {route.totalDistanceKm} km
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: alpha(palette.warning, 0.05),
                      borderRadius: theme.radius.xs,
                      border: `1px solid ${alpha(palette.warning, 0.2)}`,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: '10px',
                      }}
                    >
                      Est. Duration
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: palette.warning,
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                      }}
                    >
                      {route.totalDurationMinutes} min
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: alpha(palette.primary, 0.05),
                      borderRadius: theme.radius.xs,
                      border: `1px solid ${alpha(palette.primary, 0.2)}`,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: '10px',
                      }}
                    >
                      Deliveries
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: palette.primary,
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                      }}
                    >
                      {route.deliveryIds?.length || 0}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: alpha(palette.success, 0.05),
                      borderRadius: theme.radius.xs,
                      border: `1px solid ${alpha(palette.success, 0.2)}`,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: '10px',
                      }}
                    >
                      Avg/Delivery
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: palette.success,
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                      }}
                    >
                      {route.deliveryIds?.length
                        ? Math.round(route.totalDurationMinutes / route.deliveryIds.length)
                        : 0}{' '}
                      min
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Card>

          {/* Origin & Driver Card */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              mb: 3,
              overflow: 'hidden',
              ...animations.slideUp(0.2),
            }}
          >
            <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOnIcon sx={{ color: palette.info }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Origin Location
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 500, color: colors.textPrimary }}>
                {route.originLocation?.name || 'Unknown'}
              </Typography>
              {route.originLocation?.address && (
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  {route.originLocation.address}
                </Typography>
              )}
            </Box>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon sx={{ color: palette.primary }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Assigned Driver
                </Typography>
              </Box>
              {route.driver ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ backgroundColor: palette.primary }}>
                    {route.driver.firstName?.[0]}
                    {route.driver.lastName?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {route.driver.firstName} {route.driver.lastName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>
                      {route.driver.email}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Chip
                  icon={<PersonIcon />}
                  label="No Driver Assigned"
                  sx={{
                    backgroundColor: alpha(palette.warning, 0.1),
                    color: palette.warning,
                    fontFamily: typography.fontFamily.mono,
                    '& .MuiChip-icon': { color: palette.warning },
                  }}
                />
              )}
            </Box>
          </Card>

          {/* Delivery Stops */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              overflow: 'hidden',
              ...animations.slideUp(0.3),
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                '&:hover': { backgroundColor: alpha(palette.primary, 0.02) },
              }}
              onClick={() => setDeliveryStopsExpanded(!deliveryStopsExpanded)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingIcon sx={{ color: palette.primary }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.wide,
                  }}
                >
                  Delivery Stops ({route.routeData?.waypoints?.length || 0})
                </Typography>
                {/* Progress indicator */}
                {deliveries.length > 0 && (
                  <Chip
                    size="small"
                    label={`${deliveryStats.completed}/${deliveryStats.total} completed`}
                    sx={{
                      ml: 1,
                      backgroundColor: alpha(
                        deliveryStats.completed === deliveryStats.total ? palette.success : palette.warning,
                        0.1
                      ),
                      color: deliveryStats.completed === deliveryStats.total ? palette.success : palette.warning,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: '10px',
                    }}
                  />
                )}
              </Box>
              <IconButton size="small">
                {deliveryStopsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={deliveryStopsExpanded}>
              <List sx={{ maxHeight: 500, overflow: 'auto', p: 0 }}>
                {(route.routeData?.waypoints || []).map((waypoint, index) => {
                  const delivery = deliveryMap.get(waypoint.deliveryId);
                  const deliveryStatus = delivery?.status || 'pending';
                  const getDeliveryStatusColor = (status: string) => {
                    switch (status) {
                      case 'delivered':
                        return palette.success;
                      case 'failed':
                        return palette.error;
                      case 'out_for_delivery':
                        return palette.warning;
                      case 'picked_up':
                        return palette.info;
                      default:
                        return palette.textMuted;
                    }
                  };
                  const statusColor = getDeliveryStatusColor(deliveryStatus);

                  return (
                    <Box key={waypoint.deliveryId}>
                      <ListItem
                        sx={{
                          py: 2,
                          backgroundColor:
                            deliveryStatus === 'delivered'
                              ? alpha(palette.success, 0.03)
                              : deliveryStatus === 'failed'
                                ? alpha(palette.error, 0.03)
                                : 'transparent',
                        }}
                        secondaryAction={
                          (route.status === 'in_progress' || route.status === 'planned') && delivery && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<SettingsIcon />}
                              onClick={() => handleOpenManageDialog(delivery, index + 1)}
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                fontSize: '11px',
                                borderColor: colors.border,
                                color: colors.textSecondary,
                                '&:hover': {
                                  borderColor: palette.primary,
                                  color: palette.primary,
                                },
                              }}
                            >
                              Manage
                            </Button>
                          )
                        }
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: alpha(statusColor, 0.1),
                              color: statusColor,
                              fontFamily: typography.fontFamily.mono,
                              fontWeight: typography.fontWeight.bold,
                              fontSize: typography.fontSize.sm,
                            }}
                          >
                            {deliveryStatus === 'delivered' ? (
                              <CheckCircleIcon sx={{ fontSize: 18 }} />
                            ) : deliveryStatus === 'failed' ? (
                              <CancelIcon sx={{ fontSize: 18 }} />
                            ) : (
                              index + 1
                            )}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {waypoint.address || `Delivery ${index + 1}`}
                              </Typography>
                              <Chip
                                size="small"
                                label={deliveryStatus.replace(/_/g, ' ').toUpperCase()}
                                sx={{
                                  height: 20,
                                  backgroundColor: alpha(statusColor, 0.1),
                                  color: statusColor,
                                  fontFamily: typography.fontFamily.mono,
                                  fontSize: '9px',
                                  fontWeight: typography.fontWeight.bold,
                                  '& .MuiChip-label': { px: 1 },
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              {delivery?.customerName && (
                                <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block' }}>
                                  {delivery.customerName}
                                </Typography>
                              )}
                              {route.routeData?.legs?.[index] && (
                                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                  {route.routeData.legs[index].distanceKm} km -{' '}
                                  {route.routeData.legs[index].durationMinutes} min
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < (route.routeData?.waypoints?.length || 0) - 1 && <Divider />}
                    </Box>
                  );
                })}
              </List>
            </Collapse>
          </Card>

          {/* Timeline Card */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              mt: 3,
              overflow: 'hidden',
              ...animations.slideUp(0.4),
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AccessTimeIcon sx={{ color: palette.primary }} />
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                }}
              >
                Timeline
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: colors.textMuted }}>
                    Created
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDateTime(route.createdAt)}
                  </Typography>
                </Box>
                {route.startedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>
                      Started
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDateTime(route.startedAt)}
                    </Typography>
                  </Box>
                )}
                {route.completedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>
                      Completed
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDateTime(route.completedAt)}
                    </Typography>
                  </Box>
                )}
                {route.createdBy && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>
                      Created By
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {route.createdBy.firstName} {route.createdBy.lastName}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Right Column - Map */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              overflow: 'hidden',
              height: 'calc(100vh - 280px)',
              minHeight: 500,
              ...animations.slideUp(0.2),
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <MapIcon sx={{ color: palette.primary }} />
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                }}
              >
                Route Map
              </Typography>
            </Box>
            <Box sx={{ height: 'calc(100% - 56px)' }}>
              <GoogleMapsWrapper
                markers={mapMarkers}
                height="100%"
                darkMode={mode === 'dark'}
                showDirections={true}
                origin={{ lat: toNum(route.originLat), lng: toNum(route.originLng) }}
                destination={
                  route.returnToOrigin
                    ? { lat: toNum(route.originLat), lng: toNum(route.originLng) }
                    : route.routeData?.waypoints?.length
                      ? {
                          lat: toNum(route.routeData.waypoints[route.routeData.waypoints.length - 1].lat),
                          lng: toNum(route.routeData.waypoints[route.routeData.waypoints.length - 1].lng),
                        }
                      : undefined
                }
                waypoints={
                  route.returnToOrigin
                    ? (route.routeData?.waypoints || []).map((wp) => ({
                        lat: toNum(wp.lat),
                        lng: toNum(wp.lng),
                      }))
                    : (route.routeData?.waypoints || []).slice(0, -1).map((wp) => ({
                        lat: toNum(wp.lat),
                        lng: toNum(wp.lng),
                      }))
                }
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Delivery Stop Management Dialog */}
      <DeliveryStopManageDialog
        open={manageDialogOpen}
        onClose={handleCloseManageDialog}
        delivery={selectedDelivery}
        stopNumber={selectedStopNumber}
        onStatusUpdated={() => {
          queryClient.invalidateQueries({ queryKey: ['route-deliveries', route?.deliveryIds] });
          queryClient.invalidateQueries({ queryKey: ['delivery-route', id] });
        }}
      />
    </Box>
  );
};

export default RouteShow;
